/**
 * JobCostsSection — admin-only Job Profitability tracker on the Quote View page.
 *
 * Per-quote we track multiple "job log" entries (date, hours run, diesel gallons,
 * optional photo of the Job Clock screen, optional notes). The summary card on top
 * uses the *current* off-road diesel $/gal (from /api/admin/settings/diesel-price)
 * to compute:
 *    Revenue   — base $ collected (excludes 3% CC surcharge)
 *    Hours     — Σ(hours)
 *    Gallons   — Σ(diesel_gallons)
 *    Diesel $  — gallons × current $/gal
 *    Net Rev   — revenue − diesel $
 *    Rev/hr    — revenue ÷ hours
 *    Net Rev/hr — net revenue ÷ hours
 *
 * Hours are NEVER converted to a labor cost — they are surfaced as a denominator
 * only, so the operator can see how much each hour of work actually earned.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Camera, Clock, Fuel, DollarSign, TrendingUp, Receipt, RefreshCw,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { API } from "@/App";
import JobExpensesSection from "@/components/JobExpensesSection";

const fmtUSD = (n) =>
  n === null || n === undefined || Number.isNaN(Number(n))
    ? "—"
    : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtNum = (n, suffix = "") =>
  n === null || n === undefined || Number.isNaN(Number(n))
    ? "—"
    : `${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}${suffix}`;

const todayIso = () => new Date().toISOString().slice(0, 10);

const blankEntry = () => ({
  id: null,
  date: todayIso(),
  hours: "",
  diesel_gallons: "",
  photo_data_url: null,
  photo_filename: null,
  notes: "",
});


/** Convert a single file into a base64 data URL, downscaling oversize images. */
const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
  });


const JobCostsSection = ({ quoteId }) => {
  const [logs, setLogs] = useState([]);
  const [profitability, setProfitability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, entry: blankEntry(), saving: false });
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null });
  const [refreshingPrice, setRefreshingPrice] = useState(false);

  const refresh = useCallback(async () => {
    if (!quoteId) return;
    setLoading(true);
    try {
      const [logsRes, profRes] = await Promise.all([
        axios.get(`${API}/quotes/${quoteId}/job-logs`),
        axios.get(`${API}/quotes/${quoteId}/profitability`),
      ]);
      setLogs(logsRes.data?.logs || []);
      setProfitability(profRes.data || null);
    } catch (e) {
      // 401/404 means non-admin viewer — silently hide
      setLogs([]);
      setProfitability(null);
    } finally {
      setLoading(false);
    }
  }, [quoteId]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleSave = async () => {
    const entry = dialog.entry;
    if (!entry.hours || Number(entry.hours) <= 0) {
      toast.error("Enter hours run for this session.");
      return;
    }
    if (entry.diesel_gallons === "" || Number(entry.diesel_gallons) < 0) {
      toast.error("Enter diesel gallons (0 is allowed).");
      return;
    }

    setDialog((p) => ({ ...p, saving: true }));
    const payload = {
      date: entry.date || todayIso(),
      hours: Number(entry.hours),
      diesel_gallons: Number(entry.diesel_gallons),
      photo_data_url: entry.photo_data_url,
      photo_filename: entry.photo_filename,
      notes: entry.notes,
    };
    try {
      if (entry.id) {
        await axios.put(`${API}/quotes/${quoteId}/job-logs/${entry.id}`, payload);
        toast.success("Job log updated");
      } else {
        await axios.post(`${API}/quotes/${quoteId}/job-logs`, payload);
        toast.success("Job log added");
      }
      setDialog({ open: false, entry: blankEntry(), saving: false });
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save job log");
      setDialog((p) => ({ ...p, saving: false }));
    }
  };

  const handleDelete = async () => {
    const id = confirmDelete.id;
    setConfirmDelete({ open: false, id: null });
    try {
      await axios.delete(`${API}/quotes/${quoteId}/job-logs/${id}`);
      toast.success("Job log removed");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error("Photo too large — max 5MB");
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      setDialog((p) => ({
        ...p,
        entry: { ...p.entry, photo_data_url: dataUrl, photo_filename: file.name },
      }));
    } catch {
      toast.error("Could not read image file");
    }
  };

  const handleRefreshPrice = async () => {
    setRefreshingPrice(true);
    try {
      const res = await axios.post(`${API}/admin/settings/diesel-price/refresh`);
      toast.success(`Diesel updated from EIA to ${fmtUSD(res.data?.price_per_gallon)}/gal`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "EIA refresh failed");
    } finally {
      setRefreshingPrice(false);
    }
  };

  if (loading && !profitability) return null;

  const prof = profitability || {};
  const margin = prof.revenue_base > 0
    ? Math.round(((prof.net_revenue || 0) / prof.revenue_base) * 1000) / 10
    : null;

  return (
    <div
      className="bg-white border border-zinc-200 rounded-lg p-5 mb-6 shadow-sm"
      data-testid="job-costs-section"
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-zinc-800 uppercase tracking-wide flex items-center gap-2">
          <Receipt className="w-4 h-4 text-[#00341d]" />
          Job Profitability
          <span className="ml-2 text-[10px] font-normal text-zinc-400 normal-case tracking-normal">
            Internal — not shown to customer
          </span>
        </h3>
        <Button
          size="sm"
          onClick={() => setDialog({ open: true, entry: blankEntry(), saving: false })}
          className="bg-[#00341d] hover:bg-[#002816] text-white rounded-sm h-8 px-3"
          data-testid="add-job-log-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Add Job Log
        </Button>
      </div>

      {/* Summary grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <SummaryStat
          icon={<DollarSign className="w-4 h-4" />}
          label="Revenue (paid)"
          value={fmtUSD(prof.revenue_base)}
          testid="stat-revenue"
        />
        <SummaryStat
          icon={<Clock className="w-4 h-4" />}
          label="Hours"
          value={fmtNum(prof.total_hours, " h")}
          testid="stat-hours"
        />
        <SummaryStat
          icon={<Fuel className="w-4 h-4" />}
          label="Diesel"
          value={fmtNum(prof.total_diesel_gallons, " gal")}
          sub={`${fmtUSD(prof.diesel_cost)} @ ${fmtUSD(prof.diesel_price_per_gallon)}/gal`}
          testid="stat-diesel"
        />
        <SummaryStat
          icon={<TrendingUp className="w-4 h-4" />}
          label="Net Revenue"
          value={fmtUSD(prof.net_revenue)}
          sub={margin !== null ? `${margin}% margin` : null}
          accent
          testid="stat-net-revenue"
        />
      </div>

      {/* Per-hour KPI strip */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="bg-zinc-50 border border-zinc-200 rounded-md px-4 py-3" data-testid="stat-rev-per-hour">
          <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">Revenue / hr</div>
          <div className="text-xl font-bold text-zinc-900 tabular-nums">{fmtUSD(prof.revenue_per_hour)}</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-md px-4 py-3" data-testid="stat-net-rev-per-hour">
          <div className="text-[11px] font-medium uppercase tracking-wider text-emerald-700">Net Revenue / hr</div>
          <div className="text-xl font-bold text-emerald-900 tabular-nums">{fmtUSD(prof.net_revenue_per_hour)}</div>
        </div>
      </div>

      {/* Cost breakdown (diesel + other expenses) */}
      {(prof.expenses_total > 0 || prof.diesel_cost > 0) && (
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-zinc-500 mb-4 px-1" data-testid="cost-breakdown">
          <span>Diesel: <span className="font-medium text-zinc-700">{fmtUSD(prof.diesel_cost)}</span></span>
          <span>Expenses: <span className="font-medium text-zinc-700">{fmtUSD(prof.expenses_total)}</span></span>
          <span>Total cost: <span className="font-medium text-zinc-700">{fmtUSD(prof.total_cost)}</span></span>
          {prof.billable_to_customer_total > 0 && (
            <span>Billable to customer: <span className="font-medium text-amber-700">{fmtUSD(prof.billable_to_customer_total)}</span></span>
          )}
        </div>
      )}

      {/* Diesel price provenance */}
      <div className="flex items-center justify-between text-[11px] text-zinc-500 mb-4 px-1">
        <div>
          Diesel rate: <span className="font-medium text-zinc-700">{fmtUSD(prof.diesel_price_per_gallon)}/gal</span>
          {" · "}
          <span className="uppercase tracking-wider">{prof.diesel_price_source || "default"}</span>
          {prof.diesel_price_updated_at && (
            <> · Updated {new Date(prof.diesel_price_updated_at).toLocaleDateString()}</>
          )}
        </div>
        <button
          type="button"
          onClick={handleRefreshPrice}
          disabled={refreshingPrice}
          className="flex items-center gap-1 text-[#00341d] hover:underline disabled:opacity-50"
          data-testid="refresh-diesel-price-btn"
        >
          <RefreshCw className={`w-3 h-3 ${refreshingPrice ? "animate-spin" : ""}`} />
          {refreshingPrice ? "Refreshing…" : "Refresh from EIA"}
        </button>
      </div>

      {/* Log entries */}
      {logs.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-zinc-200 rounded-md" data-testid="no-job-logs">
          <p className="text-sm text-zinc-500">No job logs yet.</p>
          <p className="text-xs text-zinc-400 mt-1">Add an entry after each session to track real profitability.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {logs.map((log) => (
            <div
              key={log.id}
              className="flex items-start gap-3 py-3 px-3 bg-zinc-50 hover:bg-zinc-100 rounded-md transition-colors"
              data-testid={`job-log-row-${log.id}`}
            >
              {log.photo_data_url ? (
                <a
                  href={log.photo_data_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0"
                  data-testid={`job-log-photo-${log.id}`}
                >
                  <img
                    src={log.photo_data_url}
                    alt="Job clock"
                    className="w-14 h-14 object-cover rounded-md border border-zinc-200"
                  />
                </a>
              ) : (
                <div className="w-14 h-14 rounded-md border border-dashed border-zinc-300 flex items-center justify-center bg-white shrink-0">
                  <Camera className="w-5 h-5 text-zinc-300" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-zinc-800">
                  {new Date(log.date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                </div>
                <div className="text-xs text-zinc-600 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtNum(log.hours, " h")}</span>
                  <span className="flex items-center gap-1"><Fuel className="w-3 h-3" /> {fmtNum(log.diesel_gallons, " gal")}</span>
                </div>
                {log.notes && (
                  <div className="text-xs text-zinc-500 mt-1 whitespace-pre-wrap">{log.notes}</div>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => setDialog({
                    open: true,
                    entry: {
                      id: log.id,
                      date: (log.date || todayIso()).slice(0, 10),
                      hours: String(log.hours ?? ""),
                      diesel_gallons: String(log.diesel_gallons ?? ""),
                      photo_data_url: log.photo_data_url || null,
                      photo_filename: log.photo_filename || null,
                      notes: log.notes || "",
                    },
                    saving: false,
                  })}
                  className="p-1.5 rounded hover:bg-zinc-200 text-zinc-600"
                  data-testid={`edit-job-log-${log.id}`}
                  aria-label="Edit"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDelete({ open: true, id: log.id })}
                  className="p-1.5 rounded hover:bg-red-100 text-zinc-600 hover:text-red-600"
                  data-testid={`delete-job-log-${log.id}`}
                  aria-label="Delete"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Job expenses (beyond diesel) — subtract from net revenue */}
      <JobExpensesSection quoteId={quoteId} onChanged={refresh} />

      {/* Add/Edit dialog */}
      <Dialog
        open={dialog.open}
        onOpenChange={(open) => !dialog.saving && setDialog((p) => ({ ...p, open }))}
      >
        <DialogContent className="max-w-md" data-testid="job-log-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight text-[#00341d]">
              {dialog.entry.id ? "Edit Job Log" : "Add Job Log"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="jl-date">Date</Label>
              <Input
                id="jl-date"
                type="date"
                value={dialog.entry.date}
                onChange={(e) => setDialog((p) => ({ ...p, entry: { ...p.entry, date: e.target.value } }))}
                data-testid="job-log-date"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="jl-hours">Hours</Label>
                <Input
                  id="jl-hours"
                  type="number"
                  step="0.1"
                  min="0"
                  inputMode="decimal"
                  value={dialog.entry.hours}
                  onChange={(e) => setDialog((p) => ({ ...p, entry: { ...p.entry, hours: e.target.value } }))}
                  placeholder="3.0"
                  data-testid="job-log-hours"
                />
              </div>
              <div>
                <Label htmlFor="jl-gallons">Diesel (gal)</Label>
                <Input
                  id="jl-gallons"
                  type="number"
                  step="0.1"
                  min="0"
                  inputMode="decimal"
                  value={dialog.entry.diesel_gallons}
                  onChange={(e) => setDialog((p) => ({ ...p, entry: { ...p.entry, diesel_gallons: e.target.value } }))}
                  placeholder="16.4"
                  data-testid="job-log-gallons"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="jl-photo">Photo (Job Clock screen)</Label>
              <Input
                id="jl-photo"
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoChange}
                data-testid="job-log-photo"
              />
              {dialog.entry.photo_data_url && (
                <div className="mt-2 flex items-center gap-2">
                  <img
                    src={dialog.entry.photo_data_url}
                    alt="Preview"
                    className="w-16 h-16 object-cover rounded-md border border-zinc-200"
                  />
                  <button
                    type="button"
                    onClick={() => setDialog((p) => ({
                      ...p,
                      entry: { ...p.entry, photo_data_url: null, photo_filename: null },
                    }))}
                    className="text-xs text-red-600 hover:underline"
                    data-testid="remove-job-log-photo"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="jl-notes">Notes (optional)</Label>
              <Textarea
                id="jl-notes"
                rows={2}
                value={dialog.entry.notes}
                onChange={(e) => setDialog((p) => ({ ...p, entry: { ...p.entry, notes: e.target.value } }))}
                placeholder="e.g. Cleared back acre, soft ground, ran skid steer"
                data-testid="job-log-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialog({ open: false, entry: blankEntry(), saving: false })}
              disabled={dialog.saving}
              className="rounded-sm"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={dialog.saving}
              className="bg-[#00341d] hover:bg-[#002816] text-white rounded-sm"
              data-testid="save-job-log-btn"
            >
              {dialog.saving ? "Saving…" : (dialog.entry.id ? "Update" : "Add Entry")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={confirmDelete.open}
        onOpenChange={(open) => !open && setConfirmDelete({ open: false, id: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this job log?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the hours, diesel gallons, and photo for this entry.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-job-log"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const SummaryStat = ({ icon, label, value, sub, accent, testid }) => (
  <div
    className={`rounded-md border px-3 py-3 ${accent ? "bg-[#00341d]/5 border-[#00341d]/30" : "bg-white border-zinc-200"}`}
    data-testid={testid}
  >
    <div className={`text-[11px] font-medium uppercase tracking-wider flex items-center gap-1 ${accent ? "text-[#00341d]" : "text-zinc-500"}`}>
      {icon} {label}
    </div>
    <div className={`text-lg font-bold tabular-nums mt-0.5 ${accent ? "text-[#00341d]" : "text-zinc-900"}`}>
      {value}
    </div>
    {sub && <div className="text-[11px] text-zinc-500 mt-0.5">{sub}</div>}
  </div>
);

export default JobCostsSection;
